// frontend/src/pages/PaychequeList/components/FilterMenu.tsx
// Filter dropdown with "Sort by" + "Payment method" sections.
// Self-contained: owns open state, outside-click, and ESC-to-close.

import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check } from "lucide-react";
import type { SortType, MethodFilter } from "../types";
import { C, outlineBtn, menuItem } from "../styles";

interface FilterMenuProps {
  sort: SortType;
  onSortChange: (sort: SortType) => void;
  methodFilter: MethodFilter;
  onMethodFilterChange: (method: MethodFilter) => void;
}

const SORT_OPTIONS: ReadonlyArray<[SortType, string]> = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["net-high", "Net pay, highest first"],
  ["net-low", "Net pay, lowest first"],
  ["name-az", "Employee A to Z"],
];

const METHOD_OPTIONS: ReadonlyArray<[MethodFilter, string]> = [
  ["all", "All methods"],
  ["cheque", "Cheque only"],
  ["direct", "Direct deposit only"],
];

export default function FilterMenu({
  sort,
  onSortChange,
  methodFilter,
  onMethodFilterChange,
}: FilterMenuProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(function () {
    if (!isOpen) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return function () {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setIsOpen(!isOpen)} style={outlineBtn}>
        Filter <ChevronDown size={10} />
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: 36,
            right: 0,
            background: C.card,
            border: "1px solid " + C.line,
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: "10px 0",
            minWidth: 240,
            zIndex: 100,
          }}
        >
          <SectionHeading>Sort by</SectionHeading>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt[0]}
              onClick={() => onSortChange(opt[0])}
              style={{ ...menuItem, justifyContent: "space-between" }}
            >
              <span>{opt[1]}</span>
              {sort === opt[0] && <Check size={14} style={{ color: C.brand }} strokeWidth={3} />}
            </button>
          ))}

          <div style={{ height: 1, background: C.line, margin: "6px 0" }} />

          <SectionHeading>Payment method</SectionHeading>
          {METHOD_OPTIONS.map((opt) => (
            <button
              key={opt[0]}
              onClick={() => onMethodFilterChange(opt[0])}
              style={{ ...menuItem, justifyContent: "space-between" }}
            >
              <span>{opt[1]}</span>
              {methodFilter === opt[0] && <Check size={14} style={{ color: C.brand }} strokeWidth={3} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div
      style={{
        padding: "4px 14px 8px",
        fontSize: 10,
        fontWeight: 700,
        color: C.ink,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}